import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { useLoader } from '../context/loaders/useLoader';
import { useToast } from '../context/toasts/useToast';
import type { Card } from '../types/kanban';

export function useLinkExistingCard(boardId: number, currentCardId: number) {
  const [cards, setCards] = useState<Card[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { getBoard, createCardLink } = useApi();
  const { withLoader } = useLoader();
  const { addNotification } = useToast();

  const fetchCards = useCallback(async () => {
    try {
      const board = await getBoard(boardId);
      const allCards: Card[] = [];
      for (const list of board.lists) {
        allCards.push(...list.cards);
      }
      setCards(allCards.filter((c) => c.id !== currentCardId));
    } catch (err) {
      console.error(err);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load board cards',
        duration: 5000,
      });
    }
  }, [getBoard, boardId, currentCardId, addNotification]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const linkCard = async (targetCardId: number): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      await withLoader(() => createCardLink(currentCardId, targetCardId), 'Linking card...');
      addNotification({
        type: 'success',
        title: 'Card Linked',
        message: 'Card linked successfully.',
        duration: 3000,
      });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to link card';
      addNotification({
        type: 'error',
        title: 'Error',
        message: msg,
        duration: 5000,
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    cards,
    isSubmitting,
    linkCard,
  };
}
